/**
 * Client-side phrase handling for printed cards and QR codes.
 *
 * Pure and DOM-free so it can be unit-tested in the node environment the root
 * Vitest config uses.
 *
 * A printed card carries the phrase but never the PIN, so the phrase is treated
 * as the equivalent of a username rather than a secret (docs/QR-considerations.md).
 * That is what makes it acceptable to put in a QR code and in a URL fragment. It
 * is a deliberate, recorded exception to "credentials never go in the URL"
 * (instructions.md §10, src/app/router.js), not a general relaxation: the PIN is
 * still typed, and nothing here ever touches it.
 */

/**
 * The origin printed on cards and encoded into their QR codes.
 *
 * Deliberately hardcoded rather than read from `window.location`. A card is a
 * physical object that outlives the browser session that produced it, and it
 * should always send a scanner to the live service. The cost is accepted and
 * only affects developers: a card printed from a dev or preview build carries a
 * QR pointing at production, where that phrase does not exist, so the scan ends
 * at the ordinary generic failure.
 */
export const KINCODE_ORIGIN = "https://kincode.uk";

/** The same origin without its scheme, for printing and reading aloud. */
export const KINCODE_DOMAIN = "kincode.uk";

/**
 * Upper bound on an accepted phrase, matching `retrieveContactsSchema` in
 * `functions/src/validation/schemas.js`. Purely a size cap — the server keeps
 * the same loose bound so that a mistyped phrase reaches the uniform failure
 * rather than a "wrong shape" hint.
 */
export const PHRASE_MAX_LENGTH = 200;

/** The separator used in the display form. */
const SEPARATOR = "-";

/** Words in a generated phrase. Mirrors `PHRASE_WORD_COUNT` on the server. */
const PHRASE_WORD_COUNT = 4;

/**
 * Normalise a phrase to its canonical form.
 *
 * **This is a deliberate copy of `normalisePhrase` in
 * `functions/src/security/phrase.js`.** The two must stay identical: the server
 * derives the HMAC lookup from its own copy, so any divergence means a card this
 * module prints resolves to a phrase the server cannot find. They cannot share a
 * module because `functions/` is a separate workspace with its own `package.json`
 * and no build step joins them, so the guard against drift is the unit test —
 * which mirrors the server's cases on purpose.
 *
 * The rules are forgiving of everything that does not change meaning: NFKC, so a
 * pasted phrase with compatibility characters matches; lower case folded with an
 * explicit locale, because `toLowerCase()` on a Turkish-locale device maps dotted
 * capital I to a character the ASCII word list no longer contains; any run of
 * spaces, hyphens, dashes or underscores collapsed to one hyphen; and anything
 * else dropped, which absorbs stray punctuation and the full stop phones like to
 * add.
 *
 * @param {string} phrase The phrase as supplied.
 * @returns {string} The canonical form, e.g. `otter-lantern-meadow-compass`.
 */
export function normalisePhrase(phrase) {
  return phrase
    .normalize("NFKC")
    .toLocaleLowerCase("en-GB")
    .trim()
    .replace(/[\s\-_‐-―]+/gu, SEPARATOR)
    .replace(/[^a-z0-9-]/gu, "")
    .replace(/-+/gu, SEPARATOR)
    .replace(/^-|-$/gu, "");
}

/**
 * Whether a normalised phrase has the shape of one KinCode issued.
 *
 * Used **only** where the check is local and no server is involved — printing a
 * card from a phrase the owner has typed back in. It must never gate the
 * retrieval form: the server's validation is deliberately loose so that a wrong
 * phrase and a malformed one fail identically, and a stricter client would
 * reintroduce exactly the shape oracle that avoids.
 *
 * @param {string} normalised A phrase already through `normalisePhrase`.
 * @returns {boolean}
 */
export function isCardPhrase(normalised) {
  const words = normalised.split(SEPARATOR);
  return words.length === PHRASE_WORD_COUNT && words.every((word) => word.length > 0);
}

/**
 * Build the retrieval URL a card's QR code encodes.
 *
 * The phrase travels in the **fragment**, not the query string. A fragment is
 * never sent to the server, so scanning a card leaves no phrase in Firebase
 * Hosting's request logs and none in a `Referer` header. That is the limit of
 * what this buys: the scanning phone's own history, the scanner app's log and
 * any URL-safety check that app performs are all outside KinCode's reach, and
 * the printed card is readable by anyone holding it. The protection that matters
 * is that the PIN is not here and is not on the card.
 *
 * @param {string} phrase The phrase, in any form; normalised here.
 * @returns {string}
 */
export function retrievalUrl(phrase) {
  const normalised = normalisePhrase(phrase);
  // A no-op for the `[a-z0-9-]` a normalised phrase reduces to, and correct if a
  // caller ever passes something else.
  return `${KINCODE_ORIGIN}/retrieve#phrase=${encodeURIComponent(normalised)}`;
}

/**
 * Read a phrase handed to the retrieval page by a scanned card.
 *
 * Accepts the fragment form the QR codes now carry and the `?phrase=` query form
 * described in the original design note, so a card printed under either scheme
 * keeps working. The fragment wins when both are present.
 *
 * Returns an empty string for anything unusable, and the caller prefills nothing
 * — silently. There is no error message by design: a junk value here is far more
 * likely a scanning artefact than a person's mistake, and an error naming the
 * problem would tell an attacker which of their guesses had the right shape.
 *
 * @param {{ hash: string, search: string }} location Usually `window.location`.
 * @returns {string} A normalised phrase, or "" when there is nothing to use.
 */
export function phraseFromLocation(location) {
  const fromHash = new URLSearchParams(location.hash.replace(/^#/u, "")).get("phrase");
  const fromQuery = new URLSearchParams(location.search).get("phrase");
  const raw = fromHash ?? fromQuery;
  if (typeof raw !== "string" || raw.length > PHRASE_MAX_LENGTH) {
    return "";
  }
  const normalised = normalisePhrase(raw);
  return normalised.length > PHRASE_MAX_LENGTH ? "" : normalised;
}
